import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { fetchDeployments } from "../api/client";
import { DeploymentOperation } from "../types";
import DataTable from "../components/DataTable";
import DateRangePicker from "../components/DateRangePicker";
import ExportButton from "../components/ExportButton";
import StatusBadge from "../components/StatusBadge";
import DetailModal from "../components/DetailModal";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, utcDaysAgo, utcToday } from "../utils/datetime";

function today(): string {
  return utcToday();
}
function thirtyDaysAgo(): string {
  return utcDaysAgo(30);
}
function formatDuration(seconds?: number): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

// An entry is `{name, type, status}` from the deployment's execution plan.
// Rows written before that was collected hold bare strings, and rows written
// while it was read from the wrong field hold neither — `String(entry)` on
// those produced a list of "[object Object]".
function parseItems(raw?: string): string[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [raw];
  }
  const describe = (entry: unknown): string => {
    if (typeof entry === "string") return entry;
    if (entry && typeof entry === "object") {
      const item = entry as Record<string, unknown>;
      const name = typeof item.name === "string" ? item.name : "";
      const kind = typeof item.type === "string" ? item.type : "";
      if (name) return kind ? `${name} (${kind})` : name;
    }
    return "";
  };
  if (Array.isArray(parsed)) return parsed.map(describe).filter(Boolean);
  const single = describe(parsed);
  return single ? [single] : [];
}

export default function DeploymentsPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [deployments, setDeployments] = useState<DeploymentOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo());
  const [dateTo, setDateTo] = useState(today());
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { limit: "1000" };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (pipelineFilter) params.pipeline_id = pipelineFilter;
    if (statusFilter) params.status = statusFilter;

    fetchDeployments(params)
      .then((data) => setDeployments(data))
      .catch((err) => setError(err.message ?? "Failed to load deployments."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, pipelineFilter, statusFilter]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pipelines = useMemo(() => {
    const seen = new Set<string>();
    return deployments
      .filter((d) => { const key = d.pipelineId; if (seen.has(key)) return false; seen.add(key); return true; });
  }, [deployments]);

  const rowKey = (d: DeploymentOperation) => d.pipelineId + d.operationTimestamp;

  const columns: ColumnDef<DeploymentOperation, string>[] = [
    {
      id: "startedAt",
      header: "Date / Time",
      accessorFn: (row) => row.startedAt || row.operationTimestamp,
      cell: ({ getValue }) => formatDate(getValue()),
    },
    { accessorKey: "pipelineName", header: "Pipeline", cell: ({ getValue }) => getValue() || "—" },
    {
      id: "stages",
      header: "Source → Target Stage",
      accessorFn: (row) =>
        row.sourceStage && row.targetStage ? `${row.sourceStage} → ${row.targetStage}` : "",
      cell: ({ row, getValue }) => {
        const stages = getValue();
        if (!stages) return <span className="text-gray-400">—</span>;
        const workspaces = [row.original.sourceWorkspaceName, row.original.targetWorkspaceName];
        return (
          <div>
            <span className="font-mono text-sm text-gray-700">{stages}</span>
            {workspaces.every(Boolean) && (
              <div className="text-xs text-gray-500">
                {workspaces[0]} → {workspaces[1]}
              </div>
            )}
          </div>
        );
      },
    },
    { accessorKey: "triggeredByUser", header: "Triggered By", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      id: "duration",
      header: "Duration",
      accessorFn: (row) => String(row.durationSeconds ?? ""),
      cell: ({ row }) => formatDuration(row.original.durationSeconds),
    },
    {
      id: "itemsDeployed",
      header: "Items Deployed",
      accessorFn: (row) => {
        const items = parseItems(row.deployedItems);
        return String(items.length);
      },
      cell: ({ row }) => {
        const items = parseItems(row.original.deployedItems);
        const key = rowKey(row.original);
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">
              {items.length}
            </span>
            {items.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setExpandedRow(key); }}
                className="text-xs text-blue-600 hover:underline"
              >
                View
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deployments</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Pipeline deployment operations — Dev → Test → Prod audit trail.
          </p>
        </div>
        <ExportButton
          endpoint="/api/deployments/export"
          params={{
            date_from: dateFrom,
            date_to: dateTo,
            pipeline_id: pipelineFilter || undefined,
            status: statusFilter || undefined,
          }}
          filename="deployments-export.csv"
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Pipeline</label>
            <select
              value={pipelineFilter}
              onChange={(e) => setPipelineFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-52"
            >
              <option value="">All Pipelines</option>
              {pipelines.map((p) => (
                <option key={p.pipelineId} value={p.pipelineId}>{p.pipelineName}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-36"
            >
              <option value="">All</option>
              <option value="Completed">Completed</option>
              <option value="Failed">Failed</option>
              <option value="InProgress">In Progress</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors self-end"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading deployments…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-500">{deployments.length} deployment{deployments.length !== 1 ? "s" : ""}</p>
          <DataTable data={deployments} columns={columns} initialPageSize={25} />
          {/* A drawer rather than a centred dialog, because reading a
              deployment's contents is a comparative act: the rows stay visible
              beside it, and a list of deployed artifacts wants the full height
              of the viewport rather than a panel capped at 90vh.
       
              It used to render here, beneath the whole table. So clicking
              "Show" on a row near the top of a twenty-five row page put the
              items it revealed a screenful below the button that revealed
              them, and the table jumped as the panel appeared. */}
          {expandedRow && (() => {
            const dep = deployments.find((d) => rowKey(d) === expandedRow);
            if (!dep) return null;
            const items = parseItems(dep.deployedItems);
            return (
              <DetailModal
                variant="drawer"
                title="Deployed items"
                subtitle={`${items.length} item${items.length !== 1 ? "s" : ""} · ${dep.pipelineName || dep.pipelineId}`}
                onClose={() => setExpandedRow(null)}
              >
                <ul className="list-inside list-disc space-y-1 text-sm text-content-muted">
                  {items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </DetailModal>
            );
          })()}
        </>
      )}
    </div>
  );
}
